'use strict';

const { identity, get } = require('lodash');
const moment = require('moment');
const {
  formatProduct,
  addIndexToProductInfo,
  formatFieldsForExport,
  addNormWeightToExport,
  addStoreGroupToExport,
  addMinMaxReferencePriceToExport,
  addForecastSalesAndVolumeToExport,
  useTimeFlexibleDataForExport,
  useLiveMarginForExport,
} = require('../helpers/workpackage-product.helpers');
const {
  getAttributeMetadata,
  removeToolStoreGroup,
} = require('../helpers/attribute-metadata.helpers');
const { formatCompetitorPriceHistoryLabel, addCompetitorIndex } = require('./helpers');
const { competitorSources } = require('../data/enums/source-types');
const numberFormats = require('../data/enums/number-formats');

function createFeatureAwareFactory(featureDecisions, toolStoreGroupConfig) {
  /**
   * Create functions for handling feature toggling based on the loaded configuration.
   * See https://owlabs.atlassian.net/wiki/spaces/RTLS/pages/1100742755/Feature+Toggle+Approach
   */
  return {
    formatProductInfo(product) {
      return formatProduct(
        product,
        featureDecisions.useAbsoluteCompetitorIndex,
        addIndexToProductInfo
      );
    },

    priceHistoryCompetitorLabel(competitor, numberFormatter) {
      // Generate the checkbox label for competitors in the price history chart
      const indexFormat = featureDecisions.useAbsoluteCompetitorIndex
        ? numberFormats.priceFormat
        : numberFormats.percent;

      return formatCompetitorPriceHistoryLabel(
        competitor,
        numberFormatter,
        indexFormat,
        addCompetitorIndex
      );
    },

    productFieldsForExport(product, selectedHistoricalPeriod) {
      let exportFields = formatFieldsForExport(product);

      if (featureDecisions.includeNormWeight) {
        exportFields = addNormWeightToExport(exportFields, product);
      }
      if (featureDecisions.useZones) {
        exportFields = addStoreGroupToExport(exportFields, product);
      }
      if (featureDecisions.displayMinMaxPenalty) {
        exportFields = addMinMaxReferencePriceToExport(exportFields, product);
      }
      if (featureDecisions.displayHistoricalDropdown) {
        exportFields = useTimeFlexibleDataForExport(
          exportFields,
          product,
          selectedHistoricalPeriod
        );
      }
      if (featureDecisions.useForecastVolume) {
        exportFields = addForecastSalesAndVolumeToExport(exportFields, product);
      }
      if (featureDecisions.marginComparisonType === 'live') {
        exportFields = useLiveMarginForExport(exportFields, product);
      }
      return exportFields;
    },

    attributeMetadataToDisplay(attributeMetadata) {
      if (!featureDecisions.useZones) {
        return getAttributeMetadata(attributeMetadata, removeToolStoreGroup, toolStoreGroupConfig);
      }
      return getAttributeMetadata(attributeMetadata, identity);
    },

    maxFutureTimeStampToDisplay() {
      const currentTimestamp = moment().unix();
      const weeks = featureDecisions.numberWeeksHistoryLookForward;
      if (weeks) {
        return moment.unix(currentTimestamp).add(weeks, 'weeks');
      }
      return null;
    },

    mergeCompetitorTypes(competitor) {
      // This method worksout what competitor types need to be merged to get data from for the export
      // If the feature toggle is true then we want to include storeGroup competitors to the export

      const objectsToMerge = [];

      if (competitor) {
        objectsToMerge.push(
          get(competitor, competitorSources.feed, {}),
          get(competitor, competitorSources.manual, {})
        );

        if (featureDecisions.displayStoreGroupCompetitors) {
          objectsToMerge.push(get(competitor, competitorSources.storeGroup, {}));
        }
      }
      return objectsToMerge;
    },
  };
}

module.exports = createFeatureAwareFactory;
